import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import BlockUi from '@availity/block-ui';
import FormLayout from '../components/layout/form-layout';
import { CustomConfirmAlert as confirmAlert } from '../components/custom-confirm-alert';
import ApiClient from "../services/api-client";
import FormSelect from '../components/form-select';
import { VIVA_VIEW_TRANSACTION } from '../util/constants';
import useStore from '../hooks/useStore';


export default function ParochosForm() {
  const [content, setContent] = useState(0);
  const [blockuiLoading, setBlockuiLoading] = useState(false);
  const { t } = useTranslation();

  const validationSchema = Yup.object().shape({
    email: Yup.string().required(t("fieldRequired")).max(80, t("maxLengthValidation", { maxlength: 80 })),
    phone: Yup.string().required(t("fieldRequired")),
    job: Yup.string().required(t("fieldRequired")),
    mobilePhone: Yup.string().required(t("fieldRequired")),
    parochosPack: Yup.object().required(t("fieldRequired")).nullable()
  });

  const { register, control, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(validationSchema)
  });

  const createProviderOrder = (packId) => {
    packId = "PACKAGE_PROVIDER_" + packId;
    ApiClient.createPaymentOrder(packId).then(
      (response) => {
        console.debug(response.data)
        if (response.data.Success) {
          window.location = VIVA_VIEW_TRANSACTION + "/web/checkout?ref=" + response.data.OrderCode + "&paymentMethod=0";
        }
      },
      error => {
        console.error(
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          error.toString()
        );
      }
    );
  };

  useEffect(() => {
    let mounted = true;
    const company = { ...useStore.getState().selectedCompany };
    ApiClient.getParochosPacksView(company.id).then(
      response => {
        if (!mounted) return;
        var parochosPacks = response.data.parochosPacks.map(element => ({
          value: element.id,
          label: element.id + " (" + element.price + "€)",
        }));
        company.hasCompleteParochosForm = true;
        localStorage.setItem("company", JSON.stringify(company));
        setContent({ parochosPacks });
      },
      error => {
        toast.error("Error: " + error.data);
        console.error(error.data);
      }
    );
    return () => { mounted = false; };
  }, []);

  const onSubmit = (object) => {
    const company = useStore.getState().selectedCompany;
    var parochosInterConnectionFormObject = JSON.parse(JSON.stringify(object));
    if (parochosInterConnectionFormObject.parochosPack) {
      parochosInterConnectionFormObject.parochosPack = parochosInterConnectionFormObject.parochosPack.value;
    }
    parochosInterConnectionFormObject.company = { id: company.id };

    confirmAlert({
      title: t("submitConfirm"),
      message: t("areYouSure"),
      buttons: [
        {
          label: t('Yes'),
          className: "accept",
          onClick: () => ApiClient.saveCompanyParochosInterConnectionForm(company.id, parochosInterConnectionFormObject).then(
            () => {
              toast.success(t("saveSucceded"));
              createProviderOrder(parochosInterConnectionFormObject.parochosPack);
            },
            error => {
              console.error(error.response);
              toast.error("Error: " + error.response.data.message);
            }
          )
        },
        {
          label: t('No')
        }
      ]
    });
  };

  return (
    <BlockUi tag="div" blocking={blockuiLoading}>
      <div className="container-fluid" style={{ padding: 0 }}>
        <form autoComplete="off" id="parochos_form" onSubmit={handleSubmit(onSubmit)}>

          <FormLayout
            title={t('applicationParochosInterconnection')}
            icon={<i className="fa fa-handshake-o"></i>}
            footer={
              <button type="submit" className="btn btn-skin btn-lg">
                <i className="fa fa-credit-card" style={{ marginRight: 8 }}></i>
                {t("SaveAndPay")}
              </button>
            }
          >

            {/* Info notice */}
            <div className="parochos-info-box mb-3">
              <div className="parochos-info-box-title">
                <i className="fa fa-info-circle" style={{ marginRight: 8 }}></i>
                {t("parochosFormMess1")}
              </div>
              <ul className="mb-1">
                <li>{t("parochosFormMess2")}</li>
                <li>{t("parochosFormMess3")}</li>
                <li>{t("parochosFormMess4")}</li>
              </ul>
              <small>*{t("parochosFormMess5")}</small>
            </div>

            {/* Fields */}
            <div className="card mainInvoiceInfoCard mb-3">
              <div className="card-header">
                <i className="fa fa-user-circle-o" style={{ marginRight: 8 }}></i>
                {t('applicationParochosInterconnection')}
              </div>
              <div className="card-body">
                <div className="row g-3">

                  <div className="col-md-4 form-group required">
                    <label className="control-label inputlabel">{t("Email")}</label>
                    <input
                      name="email"
                      type="text"
                      autoComplete="off"
                      {...register('email', { required: true, maxLength: 80 })}
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                    />
                    <div className="invalid-feedback">{errors.email?.message}</div>
                  </div>

                  <div className="col-md-4 form-group required">
                    <label className="control-label inputlabel">{t("MobilePhone")}</label>
                    <input
                      name="mobilePhone"
                      type="text"
                      autoComplete="off"
                      {...register('mobilePhone', { required: true, maxLength: 30 })}
                      className={`form-control ${errors.mobilePhone ? 'is-invalid' : ''}`}
                    />
                    <div className="invalid-feedback">{errors.mobilePhone?.message}</div>
                  </div>

                  <div className="col-md-4 form-group required">
                    <label className="control-label inputlabel">{t("Phone")}</label>
                    <input
                      name="phone"
                      type="text"
                      autoComplete="off"
                      {...register('phone', { required: true, maxLength: 30 })}
                      className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                    />
                    <div className="invalid-feedback">{errors.phone?.message}</div>
                  </div>

                  <div className="col-md-4 form-group required">
                    <label className="control-label inputlabel">{t("Job")}</label>
                    <input
                      name="job"
                      type="text"
                      autoComplete="off"
                      {...register('job', { required: true, maxLength: 128 })}
                      className={`form-control ${errors.job ? 'is-invalid' : ''}`}
                    />
                    <div className="invalid-feedback">{errors.job?.message}</div>
                  </div>

                  <div className="col-md-4 selectInputContainer form-group required">
                    <label className="control-label inputlabel">{t("ParochosPack")}</label>
                    <Controller
                      render={({ field }) => (
                        <FormSelect
                          field={field}
                          options={content.parochosPacks}
                          error={errors.parochosPack}
                          placeholder={t('Select')}
                        />
                      )}
                      control={control}
                      name="parochosPack"
                      defaultValue=""
                    />
                    <div className="invalid-feedback">{errors.parochosPack?.message}</div>
                  </div>

                </div>
              </div>
            </div>

          </FormLayout>

        </form>
      </div>
    </BlockUi>
  );
}
